import { isEmpty, omit, isNull } from "lodash";
import { v4 as uuid } from "uuid";
import { singleValue, orderStatus } from "../../../utils";
import { getItemCost } from "../../../utils/SSCalculation";
import { resetItemOnSelection } from "./index";
import itemMappings from "../SSItem/itemMappings";

export const prepareInitialData = (data = []) => {
  const updatedData = [];
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      updatedData.push({
        ...item,
        AdjCost: isNull(item?.AdjCost) ? 0 : item?.AdjCost,
        AdjQty: isNull(item?.AdjQty) ? 0 : item?.AdjQty,
        _type: itemMappings[item?.Type] || item?.Type,
        selection: false,
        virtualLoading: true,
        _id: uuid(),
        __original: {
          SelectedItem: item?.SelectedItem,
        },
      });
    }
    return updatedData;
  }
  return data;
};

export const checkItemsBeforeSave = (
  dispatch,
  showNotifier,
  isSelected = true,
  itemList = [],
  selections = [],
  isOrderRelatedAction = false
) => {
  let isValidSaveData = true;
  // If no 'Selected' field value preset in settings form.
  if (!isSelected) {
    for (let i = 0, len = itemList.length; i < len; i++) {
      const item = itemList[i] || {};
      if (selections.includes(item._id)) {
        if (
          isOrderRelatedAction &&
          [singleValue.no].includes(item?.SelectedItem) &&
          [singleValue.no].includes(item?.__original.SelectedItem)
        ) {
          isValidSaveData = false;
          dispatch(
            showNotifier({
              message:
                "Few checked items are not saved or marked as selected 'N'. Please verify 'Sel' field.",
              severity: "error",
            })
          );
          break;
        }
      }
    }
  }
  return isValidSaveData;
};

export const prepareSettingsSaveData = (
  selected,
  storeId,
  userId,
  items = [],
  selections = []
) => {
  let updatedDetails = {
    storeId,
    userId,
    itemList: [],
  };
  if (items.length && selections.length) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i] || {};
      if (selections.includes(item?._id)) {
        let noSelection = selected === singleValue.no;
        if (!selected) {
          noSelection = item?.SelectedItem === singleValue.no;
        }
        // Get calculated values of item.
        const _item = getItemCost(item, noSelection, !noSelection);

        let updatedItem = {
          ...omit(item, [
            "__original",
            "virtualLoading",
            "selection",
            "Tier",
            "_id",
          ]),
          SelectedItem: !!selected ? selected : item?.SelectedItem,
          AdjQty: !noSelection ? item?.AdjQty || _item?.AdjQty : _item?.AdjQty,
          AdjCost: !noSelection
            ? item?.AdjCost || _item?.AdjCost
            : _item?.AdjCost,
          PONumber: !noSelection
            ? item?.PONumber || _item?.PONumber
            : _item?.PONumber,
          ReleaseDate: !noSelection
            ? item?.ReleaseDate || _item?.ReleaseDate
            : _item?.ReleaseDate,
          UserId: userId,
          StoreId: storeId,
        };
        updatedDetails = {
          ...updatedDetails,
          itemList: [...updatedDetails.itemList, updatedItem],
        };
        if (selections.length === updatedDetails.itemList.length) {
          break;
        }
      }
    }
  }
  return updatedDetails;
};

export const postSettingsSaveUpdateData = (
  selected,
  items,
  selections = []
) => {
  const updatedPogs = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (!isEmpty(selected)) {
        item = { ...item, SelectedItem: selected };
        item = {
          ...resetItemOnSelection(item),
        };
      }
      if (item?.AdjQty === 0) {
        item = {
          ...item,
          PONumber: null,
          ReleaseDate: null,
        };
      }
      updatedPogs.push(item);
    }
  }
  return updatedPogs;
};

export const saveUpdatedItems =
  (postSaveUpdatedItems, keepSelection = false) =>
  (oldItemDetails) => {
    let _updates = [];
    for (let i = 0, len = oldItemDetails?.length; i < len; i++) {
      const item = oldItemDetails[i] || {};
      const updatedPogDetails = postSaveUpdatedItems?.find(
        (updatedItem) => updatedItem?._id === item?._id
      );
      if (updatedPogDetails) {
        _updates.push({
          ...updatedPogDetails,
          selection: keepSelection ? item?.selection : false, // removing selections after saving data.
        });
      } else {
        _updates.push(item);
      }
    }
    return _updates;
  };

export const prepareInlineSaveData = (
  item,
  storeId,
  userId,
  isItemsDepartment = false
) => {
  let noSelection = item?.SelectedItem === singleValue.no;
  // Get calculated values
  const _item = getItemCost(item, noSelection, !noSelection);
  return {
    itemList: [
      {
        ...omit(item, [
          "__original",
          "virtualLoading",
          "selection",
          "Tier",
          "_id",
        ]),
        AdjQty: !noSelection ? item?.AdjQty || _item?.AdjQty : _item?.AdjQty,
        AdjCost: !noSelection
          ? item?.AdjCost || _item?.AdjCost
          : _item?.AdjCost,
        PONumber: !noSelection
          ? item?.PONumber || _item?.PONumber
          : _item?.PONumber,
        ReleaseDate: !noSelection
          ? item?.ReleaseDate || _item?.ReleaseDate
          : _item?.ReleaseDate,
        UserId: userId,
        StoreId: storeId,
        StoreItemId: isItemsDepartment ? item?.UID : item?.StoreItemId,
      },
    ],
  };
};

export const postInlineSaveUpdateData = (updateItem, items) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (item?._id === updateItem?._id) {
      item = {
        ...updateItem,
        PONumber: updateItem?.AdjQty > 0 ? updateItem.PONumber : null,
        ReleaseDate: updateItem?.AdjQty > 0 ? updateItem.ReleaseDate : null,
        __original: {
          SelectedItem: updateItem?.SelectedItem,
        },
      };
    }
    updatedItems.push(item);
  }
  return updatedItems;
};

export const saveUpdatedInlineItems =
  (postSaveUpdatedItems) => (oldItemDetails) => {
    let _updates = [];
    for (let i = 0, len = oldItemDetails?.length; i < len; i++) {
      const item = oldItemDetails[i] || {};
      const updatedItemDetails = postSaveUpdatedItems?.find(
        (updatedItem) => updatedItem?.StoreItemId === item?.StoreItemId
      );
      if (updatedItemDetails) {
        _updates.push({
          ...updatedItemDetails,
          selection: false, // removing selections after saving data.
        });
      } else {
        _updates.push(item);
      }
    }
    return _updates;
  };

const validPO = (formData, mapping, _item) => {
  let isValidPO = true;
  if (!mapping || !formData?.generatePOBy) {
    return isValidPO;
  }
  const INVALID_PO = {
    Department: "RCK ITEMDP",
    POG: "RCK ITEM99",
    Custom: formData?.customPONumber ?? "",
  };
  //If same item is selected for same PO number, then skip.
  if (
    mapping?.PONumber?.includes(INVALID_PO[formData?.generatePOBy]) &&
    _item?.PONumber?.length &&
    _item?.PONumber === INVALID_PO[formData?.generatePOBy]
  ) {
    return isValidPO;
  }
  if (mapping?.PONumber?.includes(INVALID_PO[formData?.generatePOBy])) {
    isValidPO = false;
  }
  return isValidPO;
};

export const checkNonReleasedDuplicateItems = (
  formData = {},
  releasedMappings = [],
  filteredData = [],
  selections = []
) => {
  let duplicateSKU = [];
  let isValidSelection = true;
  for (let i = 0, len = filteredData.length; i < len; i++) {
    const _item = filteredData[i];

    if (selections.length > 1) {
      if (selections.includes(_item?._id)) {
        duplicateSKU.push(_item?.StoreItemId);
      }

      if (new Set(duplicateSKU).size !== duplicateSKU.length) {
        isValidSelection = false;
        break;
      }
    }

    // Check if item is selected and its not part of released item mapping having same PO.
    if (
      !isEmpty(releasedMappings) &&
      selections.includes(_item?._id) &&
      !validPO(formData, releasedMappings[_item?.StoreItemId], _item)
    ) {
      isValidSelection = false;
      break;
    }
  }
  return isValidSelection;
};

export const getReleasedItemsWithPONumberMappings = (itemList = []) => {
  let mappings = {};
  for (let i = 0, len = itemList.length; i < len; i++) {
    let item = itemList[i];
    if (item.ReleaseStatus === orderStatus.released || !!item?.PONumber) {
      mappings = {
        ...mappings,
        [item.StoreItemId]: {
          PONumber: mappings[item.StoreItemId]?.PONumber
            ? [...mappings[item.StoreItemId]?.PONumber, item.PONumber]
            : [item.PONumber],
        },
      };
    }
  }
  return mappings;
};

export const prepareCreatePOData = (
  urlParams,
  storeId,
  formData,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  const updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    poNumber: formData?.customPONumber,
    createPOBy:
      formData?.generatePOBy === "Department" ? "DEPT" : formData?.generatePOBy,
    userId,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postCreatePOUpdateData = (
  items,
  details = [],
  selections = []
) => {
  const preparedItemList = [];
  if (selections.length && details.length) {
    for (let i = 0, len = items.length; i < len; i++) {
      let item = items[i] || {};
      if (selections.includes(item?._id)) {
        const itemWithPoNumber = details.find(
          (i) => i?.SkuNumber === item?.SKU
        );
        preparedItemList.push({
          ...item,
          PONumber: itemWithPoNumber?.PONumber,
        });
      }
    }
  }
  return preparedItemList;
};

// TODO Update accordingly.

export const prepareSaveDatesData = (
  urlParams,
  date,
  storeId,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    userId,
    releaseDate: date,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postSaveDatesUpdateData = (date, items, selections = []) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (date !== "" && item.SelectedItem === singleValue.yes) {
        item = { ...item, ReleaseDate: date };
      }
      updatedItems.push(item);
    }
  }
  return updatedItems;
};

export const prepareDeleteDatesData = (
  urlParams,
  storeId,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    PogNumber: urlParams.pogId,
    userId,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postDeleteDatesUpdateData = (items, selections = []) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (item.SelectedItem === singleValue.yes) {
        item = { ...item, ReleaseDate: null };
      }
      updatedItems.push(item);
    }
  }
  return updatedItems;
};
